import React, { useMemo, useState } from 'react';
import { Button } from '~/shadcn/components/ui/Button';
import { FilterChip } from './FilterChip.jsx';
import { FilterType } from './FilterType';

const MAX_VISIBLE_CHIPS = 4;

// Helpers
function getFilterConfigByKey(filterConfigs, key) {
    return filterConfigs.find((config) => (config.filterKey ?? config.key) === key);
}

function getOptionLabel(options = [], value) {
    const stringValue = value == null ? '' : String(value);
    const match = (options || []).find((opt) => String(opt?.value ?? opt) === stringValue);
    return match?.label ?? (value == null ? '' : String(value));
}

function buildActiveFiltersFromValues(values = {}, filterConfigs = []) {
    const filters = [];
    for (const cfg of filterConfigs) {
        const key = cfg.key ?? cfg.filterKey;
        const type = cfg.type;
        const label = cfg.label ?? key;
        const value = values?.[key];

        if (type === FilterType.TEXT) {
            if (Array.isArray(value)) {
                for (const v of value) {
                    if (v != null && String(v).trim() !== '') {
                        filters.push({ type, filterKey: key, value: v, chipLabel: `${label}: ${String(v)}` });
                    }
                }
            } else if (value && String(value).trim() !== '') {
                filters.push({ type, filterKey: key, chipLabel: `${label}: ${String(value)}` });
            }
            continue;
        }

        if (type === FilterType.NUMBER) {
            if (Array.isArray(value)) {
                for (const v of value) {
                    if (v != null && v !== '') {
                        filters.push({ type, filterKey: key, value: v, chipLabel: `${label}: ${String(v)}` });
                    }
                }
            } else if (value != null && value !== '') {
                filters.push({ type, filterKey: key, chipLabel: `${label}: ${String(value)}` });
            }
            continue;
        }

        if (type === FilterType.SELECT) {
            const isMulti = cfg.multi === true;
            if (isMulti) {
                if (Array.isArray(value) && value.length > 0) {
                    for (const v of value) {
                        const display = getOptionLabel(cfg.options, v);
                        filters.push({ type, filterKey: key, value: v, chipLabel: `${label}: ${display}` });
                    }
                }
            } else {
                if (value != null && value !== '') {
                    const display = getOptionLabel(cfg.options, value);
                    filters.push({ type, filterKey: key, chipLabel: `${label}: ${display}` });
                }
            }
            continue;
        }

        if (type === FilterType.DATE_RANGE) {
            const single = value?.date ?? undefined;
            const start = value?.startDate;
            const end = value?.endDate;
            const formatDate = (dateValue) =>
                cfg.includeTime
                    ? new Date(dateValue).toLocaleString([], {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                      })
                    : new Date(dateValue).toLocaleDateString();
            if (single) {
                filters.push({ type, filterKey: key, chipLabel: `${label}: ${formatDate(single)}` });
            } else if (start && end) {
                const startStr = formatDate(start);
                const endStr = formatDate(end);
                filters.push({ type, filterKey: key, chipLabel: `${label}: ${startStr} - ${endStr}` });
            } else if (start) {
                filters.push({ type, filterKey: key, chipLabel: `${label}: >= ${formatDate(start)}` });
            } else if (end) {
                filters.push({ type, filterKey: key, chipLabel: `${label}: <= ${formatDate(end)}` });
            }
            continue;
        }
    }
    return filters;
}

export const FilterChipContainer = ({
    values = {}, // backwards-compat: treated as appliedValues if no explicit appliedValues provided
    appliedValues: appliedValuesProp,
    draftValues = undefined,
    filterConfigs = [],
    onApply,
    onDelete,
    onChipClick,
    className = '',
}) => {
    const [showLess, setShowLess] = useState(true);

    // Create a map of filter types to their order in filterConfigs
    const filterOrder = useMemo(
        () =>
            filterConfigs.reduce((acc, config, index) => {
                acc[config.type] = index;
                return acc;
            }, {}),
        [filterConfigs],
    );

    // Determine applied values (explicit prop wins, else fallback to legacy `values`)
    const appliedValues = appliedValuesProp ?? values ?? {};

    // Build chips from raw values (applied)
    const appliedChips = useMemo(
        () => buildActiveFiltersFromValues(appliedValues, filterConfigs),
        [appliedValues, filterConfigs],
    );

    // Build chips from draft when provided
    const draftChips = useMemo(
        () => (draftValues ? buildActiveFiltersFromValues(draftValues, filterConfigs) : undefined),
        [draftValues, filterConfigs],
    );

    // Helper: map config by key
    const configByKey = useMemo(() => {
        const map = new Map();
        for (const cfg of filterConfigs) {
            const key = cfg.key ?? cfg.filterKey;
            map.set(key, cfg);
        }
        return map;
    }, [filterConfigs]);

    // Identify chip identity: for multi-value items include value, otherwise a single marker
    const getChipId = (chip) => {
        const cfg = configByKey.get(chip.filterKey);
        const isMulti =
            Array.isArray(draftValues?.[chip.filterKey]) ||
            Array.isArray(appliedValues?.[chip.filterKey]) ||
            (cfg?.type === FilterType.SELECT && cfg?.multi);
        const valuePart = isMulti ? String(chip.value) : '__single__';
        return `${chip.filterKey}::${valuePart}`;
    };

    // Compute inline diff when draft exists
    const inlineChips = useMemo(() => {
        if (!draftChips) return appliedChips.map((c) => ({ chip: c, status: 'applied' }));

        const appliedById = new Map();
        for (const c of appliedChips) appliedById.set(getChipId(c), c);
        const draftById = new Map();
        for (const c of draftChips) draftById.set(getChipId(c), c);

        const result = [];
        const changedSingletonIds = new Set();

        // Added and changed
        for (const [id, dChip] of draftById.entries()) {
            const aChip = appliedById.get(id);
            if (!aChip) {
                result.push({ chip: dChip, status: 'added' });
                continue;
            }
            // For singleton chips, if label differs treat as remove old + add new
            if (id.endsWith('__single__') && aChip.chipLabel !== dChip.chipLabel) {
                result.push({ chip: aChip, status: 'removed' });
                result.push({ chip: dChip, status: 'added' });
                changedSingletonIds.add(id);
            } else {
                result.push({ chip: aChip, status: 'applied' });
            }
        }

        // Removed (present in applied but not in draft)
        for (const [id, aChip] of appliedById.entries()) {
            if (!draftById.has(id)) {
                result.push({ chip: aChip, status: 'removed' });
            } else if (changedSingletonIds.has(id)) {
                // already handled above
            }
        }

        return result;
    }, [appliedChips, draftChips, getChipId]);

    // Sort by filter type order; keep stable within same type
    const sortedFilters = useMemo(() => {
        const arr = [...inlineChips];
        arr.sort((a, b) => {
            const orderA = filterOrder[a.chip.type] ?? Number.MAX_VALUE;
            const orderB = filterOrder[b.chip.type] ?? Number.MAX_VALUE;
            return orderA - orderB;
        });
        return arr;
    }, [inlineChips, filterOrder]);

    const visibleFilters = showLess ? sortedFilters.slice(0, MAX_VISIBLE_CHIPS) : sortedFilters;
    const remainingCount = sortedFilters.length - MAX_VISIBLE_CHIPS;

    const handleClearAll = () => {
        const deletableFilters = sortedFilters.filter((filter) => {
            const config = getFilterConfigByKey(filterConfigs, filter.chip.filterKey);
            return config && config.deletable;
        });
        if (deletableFilters.length > 0 && onDelete) {
            // Map back to original chip objects expected by onDelete
            onDelete(deletableFilters.map((f) => f.chip));
        }
    };

    const anyDeletable = useMemo(
        () =>
            sortedFilters.some((entry) => {
                const config = getFilterConfigByKey(filterConfigs, entry.chip.filterKey);
                return config && config.deletable;
            }),
        [sortedFilters, filterConfigs],
    );

    const overflowTitle = useMemo(
        () =>
            sortedFilters
                .slice(MAX_VISIBLE_CHIPS)
                .map((e) => {
                    const label =
                        e.status === 'changed' && e.prev
                            ? `${e.prev.chipLabel} → ${e.chip.chipLabel}`
                            : e.chip.chipLabel;
                    if (e.status === 'added') return `+ ${label}`;
                    if (e.status === 'removed') return `− ${label}`;
                    if (e.status === 'changed') return `↦ ${label}`;
                    return label;
                })
                .filter(Boolean)
                .join(', '),
        [sortedFilters],
    );

    const hasDraftChanges = useMemo(
        () => !!draftValues && inlineChips.some((e) => e.status !== 'applied'),
        [inlineChips, draftValues],
    );

    // Empty state (after hooks to preserve hook order across renders)
    if (!sortedFilters || sortedFilters.length === 0) {
        return (
            <div className={`p-3 flex items-center gap-2 min-h-[56px] ${className}`}>
                <span className="text-muted-foreground italic text-sm">No filters applied</span>
            </div>
        );
    }

    return (
        <div className={`p-3 flex flex-wrap items-center gap-2 min-h-[56px] ${className}`}>
            {visibleFilters.map((entry, index) => {
                const filter = entry.chip;
                const config = getFilterConfigByKey(filterConfigs, filter.filterKey);
                if (!config || !filter.chipLabel) return null;

                const key = `${filter.filterKey || filter.type}-${entry.status}-${index}`;

                // Visual styling for inline preview statuses
                let classNameStatus = '';
                let label = filter.chipLabel;
                let title = undefined;
                if (entry.status === 'added') {
                    classNameStatus = 'border border-green-600 border-dashed text-green-700';
                    title = 'Not applied yet (will be added)';
                    label = `➕ ${label}`;
                } else if (entry.status === 'removed') {
                    classNameStatus = 'border border-red-600 line-through opacity-70 text-red-700';
                    title = 'Not applied yet (will be removed)';
                    label = `➖ ${label}`;
                } else if (entry.status === 'changed') {
                    classNameStatus = 'border border-amber-600 border-dashed';
                    title = 'Not applied yet (will change)';
                    const prevLabel = entry.prev?.chipLabel ?? '';
                    label = `↦ ${prevLabel} → ${label}`;
                }

                return (
                    <FilterChip
                        key={key}
                        label={label}
                        title={title}
                        className={classNameStatus}
                        deletable={!!config.deletable}
                        onClick={() => onChipClick && onChipClick(filter)}
                        onDelete={() => onDelete && onDelete(filter)}
                    />
                );
            })}

            {showLess && remainingCount > 0 && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    title={overflowTitle}
                    onClick={() => setShowLess(false)}
                >
                    +{remainingCount} more
                </Button>
            )}

            {!showLess && sortedFilters.length > MAX_VISIBLE_CHIPS && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowLess(true)}>
                    Show less
                </Button>
            )}

            {anyDeletable && hasDraftChanges && (
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-7 px-2 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => onApply && onApply(draftValues)}
                >
                    Apply
                </Button>
            )}

            {anyDeletable && (
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-7 px-2 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200"
                    onClick={handleClearAll}
                >
                    Clear
                </Button>
            )}
        </div>
    );
};
